'use client';

import { cva, type VariantProps } from 'class-variance-authority';
import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';

import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '../../primitives/card';
import { cn } from '../../utils/cn';

const cardVariants = cva('', {
  variants: {
    padding: {
      none: '',
      sm: 'p-3',
      default: '',
      lg: 'p-8',
    },
    surface: {
      default: '',
      muted: 'bg-muted/40',
      ghost: 'border-0 shadow-none bg-transparent',
    },
  },
  defaultVariants: { padding: 'default', surface: 'default' },
});

export interface UzCardProps
  extends Omit<HTMLAttributes<HTMLDivElement>, 'title'>, VariantProps<typeof cardVariants> {
  title?: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  footer?: ReactNode;
}

export const UzCard = forwardRef<HTMLDivElement, UzCardProps>(
  (
    { title, description, actions, footer, padding, surface, className, children, ...rest },
    ref,
  ) => {
    const hasHeader = !!title || !!description || !!actions;
    return (
      <Card ref={ref} className={cn(cardVariants({ padding, surface }), className)} {...rest}>
        {hasHeader && (
          <CardHeader className={cn('flex flex-row items-start justify-between gap-4 space-y-0')}>
            <div className="space-y-1.5">
              {title && <CardTitle className="text-lg">{title}</CardTitle>}
              {description && <CardDescription>{description}</CardDescription>}
            </div>
            {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
          </CardHeader>
        )}
        {children !== undefined && (
          <CardContent className={cn(!hasHeader && 'pt-6')}>{children}</CardContent>
        )}
        {footer && <CardFooter className="border-t pt-4">{footer}</CardFooter>}
      </Card>
    );
  },
);
UzCard.displayName = 'UzCard';
